import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";

/**
 * Indique si une demande de levée de sanction est en attente pour
 * l'inscription (bloc `demandeSuspensionRemobilisation` servi par l'API sur la ligne de
 * l'allocataire — null quand aucune demande À TRAITER n'existe).
 */
export const aDemandeSuspensionRemobilisation = (inscription) =>
  Boolean(inscription?.demandeSuspensionRemobilisation);

const formatDate = (iso) =>
  iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy") : null;

// Compteur module : ids uniques pour relier chaque modale à son titre.
let nbModales = 0;

/**
 * Modale de détail d'une demande de levée. Le justificatif vient de
 * l'extérieur (France Travail aujourd'hui) : sa longueur n'est pas maîtrisée,
 * d'où un bloc scrollable plutôt qu'un tooltip ou un texte libre.
 */
const ModalDemandeSuspensionRemobilisation = ({ demandeSuspensionRemobilisation, allocataireNom, onClose }) => {
  const date = formatDate(demandeSuspensionRemobilisation?.dateDemande);
  const [titreId] = useState(() => `modal-demande-suspension-remobilisation-${++nbModales}`);

  // Fenêtre posée par-dessus la modale de décision (hors CustomModal) : elle
  // porte elle-même la sémantique de dialogue et se ferme à Échap.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  return (
    <div
      className="position-fixed d-flex align-items-center justify-content-center"
      style={{
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.5)",
        zIndex: 10002,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="bg-white rounded shadow p-4"
        style={{ maxWidth: 560, width: "90%" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titreId}
        onClick={(e) => e.stopPropagation()}
      >
        <h5
          id={titreId}
          className="mb-1"
          style={{ fontSize: 18, color: "#1e293b", fontWeight: 700 }}
        >
          <i className="fa fa-unlock-alt mr-2" style={{ color: "#5b21b6" }} aria-hidden="true" />
          Demande de levée de sanction
        </h5>
        {allocataireNom && (
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
            {allocataireNom}
          </p>
        )}

        <div
          className="d-flex flex-wrap mb-3"
          style={{ gap: 6, fontSize: 13, color: "#1e293b" }}
        >
          <span
            className="px-2 py-1 rounded"
            style={{ background: "#ede9fe", color: "#5b21b6", fontWeight: 600 }}
          >
            <i className="fa fa-calendar mr-1" style={{ fontSize: 11 }} aria-hidden="true" />
            {date ? `Déposée le ${date}` : "Date de dépôt inconnue"}
          </span>
          {demandeSuspensionRemobilisation?.origineLabel && (
            <span
              className="px-2 py-1 rounded"
              style={{
                background: "#f1f5f9",
                color: "#475569",
                fontWeight: 600,
              }}
            >
              <i className="fa fa-building mr-1" style={{ fontSize: 11 }} aria-hidden="true" />
              {demandeSuspensionRemobilisation.origineLabel}
            </span>
          )}
        </div>

        <p
          id={`${titreId}-motif`}
          style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}
          className="mb-1 d-block"
        >
          Motif de la demande
        </p>
        <div
          className="p-3 rounded mb-4"
          role="region"
          aria-labelledby={`${titreId}-motif`}
          tabIndex={0}
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            fontSize: 13,
            color: "#334155",
            maxHeight: "40vh",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {demandeSuspensionRemobilisation?.justificatif || (
            <em style={{ color: "#94a3b8" }}>
              Aucun motif transmis avec la demande.
            </em>
          )}
        </div>

        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-sm"
            style={{ background: "#5b21b6", color: "#fff", fontWeight: 600 }}
            onClick={onClose}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Badge signalant qu'une demande de levée de sanction a été déposée pour
 * l'allocataire (par France Travail aujourd'hui, par ESPOAR demain), afin que
 * les agents le sachent avant de finaliser la sanction concernée. Cliquable :
 * ouvre la modale de détail avec le motif intégral — un tooltip ne convient
 * pas, le motif est long et non maîtrisé. L'affichage conditionnel est à la
 * charge de l'appelant, via aDemandeSuspensionRemobilisation.
 *
 * - allocataireNom : rappel du nom dans la modale (optionnel)
 * - fontSize : taille du badge (11 par défaut, 12 dans la bannière de décision)
 */
const BadgeDemandeSuspensionRemobilisation = ({
  demandeSuspensionRemobilisation,
  allocataireNom,
  fontSize = 11,
}) => {
  const [ouverte, setOuverte] = useState(false);

  return (
    <>
      <button
        type="button"
        className="d-inline-flex align-items-center px-2 py-1 rounded border-0"
        style={{
          background: "#ede9fe",
          color: "#5b21b6",
          fontSize,
          fontWeight: 600,
          gap: 4,
          cursor: "pointer",
        }}
        title="Voir la demande de levée"
        aria-haspopup="dialog"
        aria-expanded={ouverte}
        onClick={(e) => {
          // Les lignes/cartes qui portent le badge sont souvent cliquables :
          // ouvrir le détail ne doit pas déclencher leur action.
          e.stopPropagation();
          setOuverte(true);
        }}
      >
        <i className="fa fa-unlock-alt" style={{ fontSize: fontSize - 1 }} aria-hidden="true" />
        Levée demandée
        <i className="fa fa-search-plus" style={{ fontSize: fontSize - 2 }} aria-hidden="true" />
      </button>
      {ouverte && (
        <ModalDemandeSuspensionRemobilisation
          demandeSuspensionRemobilisation={demandeSuspensionRemobilisation}
          allocataireNom={allocataireNom}
          onClose={() => setOuverte(false)}
        />
      )}
    </>
  );
};

/**
 * Bannière pour la modale de décision : au moment de statuer, les agents
 * voient la demande sans clic supplémentaire. Le motif est borné en hauteur
 * (contenu externe non maîtrisé) et scrollable au-delà.
 */
export const BanniereDemandeSuspensionRemobilisation = ({ demandeSuspensionRemobilisation }) => {
  const date = formatDate(demandeSuspensionRemobilisation?.dateDemande);

  return (
    <div
      className="d-flex px-4 py-2"
      style={{
        background: "linear-gradient(135deg, #ede9fe, #ddd6fe)",
        gap: 10,
      }}
    >
      <i
        className="fa fa-unlock-alt"
        style={{ fontSize: 16, color: "#5b21b6", marginTop: 2 }}
        aria-hidden="true"
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 13, color: "#5b21b6", fontWeight: 700 }}>
          Demande de levée de sanction
          {date ? ` déposée le ${date}` : ""}
          {demandeSuspensionRemobilisation?.origineLabel
            ? ` — ${demandeSuspensionRemobilisation.origineLabel}`
            : ""}
        </span>
        {demandeSuspensionRemobilisation?.justificatif && (
          <div
            style={{
              fontSize: 12,
              color: "#6d28d9",
              marginTop: 2,
              maxHeight: 72,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {demandeSuspensionRemobilisation.justificatif}
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeDemandeSuspensionRemobilisation;
